// First Created - 07 Oct 2026
// Author - Atul Pandey
// Purpose - Create Org Trail Session Q&A Assignment


import React, {
    useEffect,
    useMemo,
    useState,
} from "react"


import {
    useNavigate,
} from "react-router"


import useTitle from "@/hooks/useTitle"


import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"


import PulseLoader
    from "react-spinners/PulseLoader"


import {
    useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"


import {
    useGetTrailsQuery,
} from "@/features/trails/trailsApiSlice"


import {
    useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"


import {
    useGetOrgTrailSessionCohortsQuery,
} from "@/pages/admin/orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice"


import {
    useGetOrgTrailSessionQNAAssignmentsQuery,
    useCreateOrgTrailSessionQNAAssignmentMutation,
} from "./orgTrailSessionQNAAssignmentsApiSlice"



/*
|--------------------------------------------------------------------------
| 01 - Resolve Populated Or Plain ID
|--------------------------------------------------------------------------
*/

const getRefId = (
    value
) => {

    if (
        !value
    ) {
        return ""
    }


    if (
        typeof value ===
        "string"
    ) {
        return value
    }


    return (
        value?._id ||
        value?.id ||
        ""
    )
}



/*
|--------------------------------------------------------------------------
| Component
|--------------------------------------------------------------------------
*/

const EtCreateOrgTrailSessionQNAAssignment = ({
    setActiveAdminView,
}) => {

    useTitle(
        "Create Session Q&A Assignment"
    )


    const navigate =
        useNavigate()



    /*
    |--------------------------------------------------------------------------
    | 02 - Initial Form State
    |--------------------------------------------------------------------------
    */

    const initialFormState = {

        orgId:
            "",

        trailId:
            "",

        cohortId:
            "",

        sessionId:
            "",

        sessionQNASetId:
            "",

        active:
            true,

    }


    const [
        formData,
        setFormData,
    ] =
        useState(
            initialFormState
        )



    /*
    |--------------------------------------------------------------------------
    | 03 - Create Mutation
    |--------------------------------------------------------------------------
    */

    const [
        createOrgTrailSessionQNAAssignment,
        {
            isLoading,
            isSuccess,
            isError,
            error,
        },
    ] =
        useCreateOrgTrailSessionQNAAssignmentMutation()



    /*
    |--------------------------------------------------------------------------
    | 04 - Organizations
    |--------------------------------------------------------------------------
    */

    const {
        data:
            organizations,

        isSuccess:
            isOrganizationsSuccess,
    } =
        useGetOrganizationsQuery(
            "organizationsList",
            {
                pollingInterval:
                    60000,

                refetchOnFocus:
                    true,

                refetchOnMountOrArgChange:
                    true,
            }
        )



    /*
    |--------------------------------------------------------------------------
    | 05 - Trails
    |--------------------------------------------------------------------------
    */

    const {
        data:
            trails,

        isSuccess:
            isTrailsSuccess,
    } =
        useGetTrailsQuery(
            "trailsList",
            {
                pollingInterval:
                    60000,

                refetchOnFocus:
                    true,

                refetchOnMountOrArgChange:
                    true,
            }
        )



    /*
    |--------------------------------------------------------------------------
    | 06 - Sessions
    |--------------------------------------------------------------------------
    */

    const {
        data:
            sessions,

        isSuccess:
            isSessionsSuccess,
    } =
        useGetSessionsQuery(
            "sessionsList",
            {
                pollingInterval:
                    60000,

                refetchOnFocus:
                    true,

                refetchOnMountOrArgChange:
                    true,
            }
        )



    /*
    |--------------------------------------------------------------------------
    | 07 - Cohorts
    |--------------------------------------------------------------------------
    */

    const {
        data:
            cohortsData,

        isSuccess:
            isCohortsSuccess,
    } =
        useGetOrgTrailSessionCohortsQuery({
            active:
                true,
        })



    /*
    |--------------------------------------------------------------------------
    | 08 - Existing Assignments
    |--------------------------------------------------------------------------
    */

    const {
        data:
            existingAssignmentsData,

        isSuccess:
            isAssignmentsSuccess,
    } =
        useGetOrgTrailSessionQNAAssignmentsQuery({
            active:
                true,
        })



    /*
    |--------------------------------------------------------------------------
    | 09 - Return To List After Success
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            if (
                isSuccess
            ) {

                setFormData(
                    initialFormState
                )


                if (
                    setActiveAdminView
                ) {

                    setActiveAdminView(
                        "orgTrailSessionQNAAssignments"
                    )

                } else {

                    navigate(
                        "/orgTrailSessionQNAAssignments"
                    )
                }
            }

        },
        [
            isSuccess,
            navigate,
            setActiveAdminView,
        ]
    )



    /*
    |--------------------------------------------------------------------------
    | 10 - Normalize Organizations
    |--------------------------------------------------------------------------
    */

    const organizationList =
        useMemo(
            () => {

                if (
                    !isOrganizationsSuccess ||
                    !organizations
                        ?.ids
                ) {
                    return []
                }


                return organizations.ids
                    .map(
                        id =>
                            organizations
                                .entities[
                                    id
                                ]
                    )
                    .filter(
                        Boolean
                    )

            },
            [
                isOrganizationsSuccess,
                organizations,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 11 - Normalize Trails
    |--------------------------------------------------------------------------
    */

    const trailList =
        useMemo(
            () => {

                if (
                    !isTrailsSuccess ||
                    !trails?.ids
                ) {
                    return []
                }


                return trails.ids
                    .map(
                        id =>
                            trails
                                .entities[
                                    id
                                ]
                    )
                    .filter(
                        Boolean
                    )

            },
            [
                isTrailsSuccess,
                trails,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 12 - Normalize Sessions
    |--------------------------------------------------------------------------
    */

    const sessionList =
        useMemo(
            () => {

                if (
                    !isSessionsSuccess ||
                    !sessions?.ids
                ) {
                    return []
                }


                return sessions.ids
                    .map(
                        id =>
                            sessions
                                .entities[
                                    id
                                ]
                    )
                    .filter(
                        Boolean
                    )

            },
            [
                isSessionsSuccess,
                sessions,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 13 - Normalize Cohorts
    |--------------------------------------------------------------------------
    */

    const cohortList =
        useMemo(
            () => {

                if (
                    !isCohortsSuccess ||
                    !cohortsData?.ids
                ) {
                    return []
                }


                return cohortsData.ids
                    .map(
                        id =>
                            cohortsData
                                .entities[
                                    id
                                ]
                    )
                    .filter(
                        Boolean
                    )

            },
            [
                isCohortsSuccess,
                cohortsData,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 14 - Normalize Existing Assignments
    |--------------------------------------------------------------------------
    */

    const existingAssignmentList =
        useMemo(
            () => {

                if (
                    !isAssignmentsSuccess ||
                    !existingAssignmentsData
                        ?.ids
                ) {
                    return []
                }


                return existingAssignmentsData.ids
                    .map(
                        id =>
                            existingAssignmentsData
                                .entities[
                                    id
                                ]
                    )
                    .filter(
                        Boolean
                    )

            },
            [
                isAssignmentsSuccess,
                existingAssignmentsData,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 15 - Cohorts For Selected Org + Trail
    |--------------------------------------------------------------------------
    */

    const filteredCohorts =
        useMemo(
            () => {

                if (
                    !formData.orgId ||
                    !formData.trailId
                ) {
                    return []
                }


                return cohortList
                    .filter(
                        cohort =>

                            getRefId(
                                cohort
                                    ?.orgId
                            ) ===
                                formData
                                    .orgId &&

                            getRefId(
                                cohort
                                    ?.trailId
                            ) ===
                                formData
                                    .trailId &&

                            cohort
                                ?.active !==
                                false
                    )
                    .sort(
                        (
                            a,
                            b
                        ) => {

                            const seqA =
                                a
                                    ?.cohortSequence ||
                                999999


                            const seqB =
                                b
                                    ?.cohortSequence ||
                                999999


                            if (
                                seqA !==
                                seqB
                            ) {
                                return (
                                    seqA -
                                    seqB
                                )
                            }


                            return (
                                a
                                    ?.cohortName ||
                                ""
                            )
                                .localeCompare(
                                    b
                                        ?.cohortName ||
                                    ""
                                )
                        }
                    )

            },
            [
                cohortList,
                formData.orgId,
                formData.trailId,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 16 - Sessions For Selected Trail
    |--------------------------------------------------------------------------
    */

    const filteredSessions =
        useMemo(
            () => {

                if (
                    !formData.trailId
                ) {
                    return []
                }


                return sessionList
                    .filter(
                        session =>

                            getRefId(
                                session
                                    ?.trailId
                            ) ===
                                formData
                                    .trailId &&

                            session
                                ?.active !==
                                false
                    )
                    .sort(
                        (
                            a,
                            b
                        ) => {

                            const seqA =
                                a
                                    ?.sessionSequence ||
                                999999


                            const seqB =
                                b
                                    ?.sessionSequence ||
                                999999


                            return (
                                seqA -
                                seqB
                            )
                        }
                    )

            },
            [
                sessionList,
                formData.trailId,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 17 - Selected Session
    |--------------------------------------------------------------------------
    */

    const selectedSession =
        useMemo(
            () => {

                return filteredSessions
                    .find(
                        session =>
                            getRefId(
                                session
                            ) ===
                            formData
                                .sessionId
                    ) ||
                    null

            },
            [
                filteredSessions,
                formData.sessionId,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 18 - Q&A Sets Attached To Selected Session
    |--------------------------------------------------------------------------
    */

    const filteredSessionQNASets =
        useMemo(
            () => {

                if (
                    !selectedSession
                ) {
                    return []
                }


                return (
                    selectedSession
                        ?.sessionQNASets ||
                    []
                )
                    .filter(
                        item =>
                            item
                                ?.active !==
                                false &&
                            item
                                ?.sessionQNASetId
                    )
                    .slice()
                    .sort(
                        (
                            a,
                            b
                        ) =>
                            (
                                a
                                    ?.sessionQNASetSequence ||
                                0
                            ) -
                            (
                                b
                                    ?.sessionQNASetSequence ||
                                0
                            )
                    )

            },
            [
                selectedSession,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 19 - Existing Assignment Match
    |--------------------------------------------------------------------------
    */

    const matchingExistingAssignment =
        useMemo(
            () => {

                if (
                    !formData.orgId ||
                    !formData.trailId ||
                    !formData.cohortId ||
                    !formData.sessionId ||
                    !formData.sessionQNASetId
                ) {
                    return null
                }


                return (
                    existingAssignmentList
                        .find(
                            assignment =>

                                getRefId(
                                    assignment
                                        ?.orgId
                                ) ===
                                    formData
                                        .orgId &&

                                getRefId(
                                    assignment
                                        ?.trailId
                                ) ===
                                    formData
                                        .trailId &&

                                getRefId(
                                    assignment
                                        ?.cohortId
                                ) ===
                                    formData
                                        .cohortId &&

                                getRefId(
                                    assignment
                                        ?.sessionId
                                ) ===
                                    formData
                                        .sessionId &&

                                getRefId(
                                    assignment
                                        ?.sessionQNASetId
                                ) ===
                                    formData
                                        .sessionQNASetId
                        ) ||
                    null
                )

            },
            [
                existingAssignmentList,
                formData,
            ]
        )



    /*
    |--------------------------------------------------------------------------
    | 20 - Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        e
    ) => {

        const {
            name,
            value,
            type,
            checked,
        } =
            e.target


        if (
            type ===
            "checkbox"
        ) {

            setFormData(
                prev => ({
                    ...prev,

                    [name]:
                        checked,
                })
            )

            return
        }


        setFormData(
            prev => ({

                ...prev,

                [name]:
                    value,


                ...(name ===
                "orgId"
                    ?
                    {
                        trailId:
                            "",

                        cohortId:
                            "",

                        sessionId:
                            "",

                        sessionQNASetId:
                            "",
                    }
                    :
                    {}),


                ...(name ===
                "trailId"
                    ?
                    {
                        cohortId:
                            "",

                        sessionId:
                            "",

                        sessionQNASetId:
                            "",
                    }
                    :
                    {}),


                ...(name ===
                "sessionId"
                    ?
                    {
                        sessionQNASetId:
                            "",
                    }
                    :
                    {}),

            })
        )
    }



    /*
    |--------------------------------------------------------------------------
    | 21 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (
            e
        ) => {

            e.preventDefault()


            if (
                !formData.orgId ||
                !formData.trailId ||
                !formData.cohortId ||
                !formData.sessionId ||
                !formData.sessionQNASetId
            ) {

                alert(
                    "Organization, Trail, Cohort, Session and Session Q&A Set are required."
                )

                return
            }


            if (
                matchingExistingAssignment
            ) {

                alert(
                    "This Session Q&A Set is already assigned to the selected cohort. Use Update instead."
                )

                return
            }


            try {

                const response =
                    await createOrgTrailSessionQNAAssignment({

                        orgId:
                            formData
                                .orgId,

                        trailId:
                            formData
                                .trailId,

                        cohortId:
                            formData
                                .cohortId,

                        sessionId:
                            formData
                                .sessionId,

                        sessionQNASetId:
                            formData
                                .sessionQNASetId,

                        active:
                            formData
                                .active,

                    })
                        .unwrap()


                alert(
                    response
                        ?.message ||
                    "Session Q&A assignment created successfully"
                )

            } catch (
                submitError
            ) {

                console.error(
                    submitError
                )


                alert(
                    submitError
                        ?.data
                        ?.message ||
                    "Failed to create Session Q&A assignment"
                )
            }
        }



    /*
    |--------------------------------------------------------------------------
    | 22 - Cancel / Clear
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {

        if (
            setActiveAdminView
        ) {

            setActiveAdminView(
                "orgTrailSessionQNAAssignments"
            )

        } else {

            navigate(
                "/orgTrailSessionQNAAssignments"
            )
        }
    }


    const handleClear = () => {

        setFormData(
            initialFormState
        )
    }



    /*
    |--------------------------------------------------------------------------
    | 23 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black"
        >

            {
                isLoading &&
                (
                    <PulseLoader
                        color={
                            "#ea580c"
                        }
                    />
                )
            }


            {
                isError &&
                (
                    <p
                        className="text-orange-700 font-semi-bold py-2"
                    >
                        {
                            error
                                ?.data
                                ?.message
                        }
                    </p>
                )
            }


            <div
                className="bg-white p-1 m-1 rounded"
            >

                <p
                    className="p-1 m-1 font-bold font-poppins text-xl"
                >
                    Add a Session Q&A Assignment
                </p>


                <div
                    className="bg-gray-200 rounded m-1 p-1"
                >

                    <form
                        onSubmit={
                            handleSubmit
                        }

                        className="m-1 p-1 text-xs font-poppins"
                    >


                        {/* Organization */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Organization
                            </p>


                            <select
                                name="orgId"

                                value={
                                    formData
                                        .orgId
                                }

                                onChange={
                                    handleChange
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            >

                                <option
                                    value=""
                                >
                                    Select Organization
                                </option>


                                {
                                    organizationList.map(
                                        organization => (

                                            <option
                                                key={
                                                    getRefId(
                                                        organization
                                                    )
                                                }

                                                value={
                                                    getRefId(
                                                        organization
                                                    )
                                                }
                                            >
                                                {
                                                    organization
                                                        ?.organizationName
                                                }
                                            </option>

                                        )
                                    )
                                }

                            </select>

                        </div>



                        {/* Trail */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Trail
                            </p>


                            <select
                                name="trailId"

                                value={
                                    formData
                                        .trailId
                                }

                                onChange={
                                    handleChange
                                }

                                disabled={
                                    !formData
                                        .orgId
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >

                                <option
                                    value=""
                                >
                                    Select Trail
                                </option>


                                {
                                    trailList.map(
                                        trail => (

                                            <option
                                                key={
                                                    getRefId(
                                                        trail
                                                    )
                                                }

                                                value={
                                                    getRefId(
                                                        trail
                                                    )
                                                }
                                            >
                                                {
                                                    trail
                                                        ?.trailName
                                                }
                                            </option>

                                        )
                                    )
                                }

                            </select>

                        </div>



                        {/* Cohort */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Cohort
                            </p>


                            <select
                                name="cohortId"

                                value={
                                    formData
                                        .cohortId
                                }

                                onChange={
                                    handleChange
                                }

                                disabled={
                                    !formData
                                        .orgId ||
                                    !formData
                                        .trailId
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >

                                <option
                                    value=""
                                >
                                    Select Cohort
                                </option>


                                {
                                    filteredCohorts.map(
                                        cohort => (

                                            <option
                                                key={
                                                    getRefId(
                                                        cohort
                                                    )
                                                }

                                                value={
                                                    getRefId(
                                                        cohort
                                                    )
                                                }
                                            >
                                                {
                                                    cohort
                                                        ?.cohortName ||
                                                    cohort
                                                        ?.cohortCode
                                                }
                                            </option>

                                        )
                                    )
                                }

                            </select>

                        </div>



                        {/* Session */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session
                            </p>


                            <select
                                name="sessionId"

                                value={
                                    formData
                                        .sessionId
                                }

                                onChange={
                                    handleChange
                                }

                                disabled={
                                    !formData
                                        .trailId
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >

                                <option
                                    value=""
                                >
                                    Select Session
                                </option>


                                {
                                    filteredSessions.map(
                                        session => (

                                            <option
                                                key={
                                                    getRefId(
                                                        session
                                                    )
                                                }

                                                value={
                                                    getRefId(
                                                        session
                                                    )
                                                }
                                            >
                                                {
                                                    session
                                                        ?.sessionSequence
                                                        ?
                                                        `${session.sessionSequence} - `
                                                        :
                                                        ""
                                                }

                                                {
                                                    session
                                                        ?.sessionName
                                                }
                                            </option>

                                        )
                                    )
                                }

                            </select>

                        </div>



                        {/* Session Q&A Set */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session Q&A Set
                            </p>


                            <p
                                className="text-[8px] m-1 italic text-gray-600"
                            >
                                Only Q&A Sets already attached to the selected Session are available.
                            </p>


                            <select
                                name="sessionQNASetId"

                                value={
                                    formData
                                        .sessionQNASetId
                                }

                                onChange={
                                    handleChange
                                }

                                disabled={
                                    !formData
                                        .sessionId
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >

                                <option
                                    value=""
                                >
                                    Select Session Q&A Set
                                </option>


                                {
                                    filteredSessionQNASets.map(
                                        (
                                            item,
                                            index
                                        ) => {

                                            const qnaSet =
                                                item
                                                    ?.sessionQNASetId


                                            const qnaSetId =
                                                getRefId(
                                                    qnaSet
                                                )


                                            return (

                                                <option
                                                    key={
                                                        qnaSetId ||
                                                        index
                                                    }

                                                    value={
                                                        qnaSetId
                                                    }
                                                >
                                                    {
                                                        item
                                                            ?.sessionQNASetSequence
                                                            ?
                                                            `${item.sessionQNASetSequence} - `
                                                            :
                                                            ""
                                                    }

                                                    {
                                                        qnaSet
                                                            ?.sessionQNAName ||
                                                        `Q&A Set ${index + 1}`
                                                    }
                                                </option>

                                            )
                                        }
                                    )
                                }

                            </select>


                            {
                                formData
                                    .sessionId &&
                                filteredSessionQNASets
                                    .length ===
                                    0 &&
                                (
                                    <div
                                        className="bg-gray-100 rounded m-1 p-2"
                                    >
                                        <p
                                            className="text-[10px] text-gray-600"
                                        >
                                            No Q&A Sets are attached to this Session.
                                        </p>
                                    </div>
                                )
                            }


                            {
                                matchingExistingAssignment &&
                                (
                                    <div
                                        className="bg-orange-50 rounded m-1 p-2"
                                    >
                                        <p
                                            className="text-[10px] text-orange-700"
                                        >
                                            This Q&A Set is already assigned to this cohort. Use Update instead.
                                        </p>
                                    </div>
                                )
                            }

                        </div>



                        {/* Active */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <div
                                className="flex justify-start items-center bg-gray-100 rounded m-1 p-2"
                            >

                                <input
                                    type="checkbox"

                                    name="active"

                                    checked={
                                        formData
                                            .active
                                    }

                                    onChange={
                                        handleChange
                                    }

                                    className="m-1"
                                />


                                <p
                                    className="text-xs m-1"
                                >
                                    Active
                                </p>

                            </div>

                        </div>



                        {/* Actions */}

                        <div
                            className="bg-white m-1 px-1 py-3 rounded"
                        >

                            <EtBtnFormBasic
                                name="Create"
                                type="create"
                                handleClick={
                                    handleSubmit
                                }
                            />


                            <EtBtnFormBasic
                                name="Clear"
                                type="clear"
                                handleClick={
                                    handleClear
                                }
                            />


                            <EtBtnFormBasic
                                name="Cancel"
                                type="cancel"
                                handleClick={
                                    handleCancel
                                }
                            />

                        </div>

                    </form>

                </div>

            </div>

        </div>
    )
}


export default EtCreateOrgTrailSessionQNAAssignment